// src/pages/ClientOpenpayCheckout.js
// Página independiente (en nueva pestaña) con el diseño oficial de Openpay Perú
// Ref: https://documents.openpay.pe/documentacion/pagos-con-tarjeta
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    CircularProgress,
    Chip,
    Alert,
    Dialog,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Check as CheckIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    ArrowDownward as DownloadIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

const loadSingleScript = (src, fallbackSrc) => {
    return new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = false;
        s.onload = () => resolve(true);
        s.onerror = () => {
            if (fallbackSrc) {
                const fb = document.createElement('script');
                fb.src = fallbackSrc;
                fb.async = false;
                fb.onload = () => resolve(true);
                fb.onerror = () => resolve(false);
                document.head.appendChild(fb);
            } else {
                resolve(false);
            }
        };
        document.head.appendChild(s);
    });
};

const loadOpenpayScripts = async () => {
    if (!window.jQuery) {
        await loadSingleScript(
            'https://ajax.googleapis.com/ajax/libs/jquery/1.11.0/jquery.min.js',
            'https://code.jquery.com/jquery-3.6.0.min.js'
        );
    }
    if (!window.OpenPay) {
        await loadSingleScript(
            'https://js.openpay.pe/openpay.v1.min.js',
            'https://openpay.s3.amazonaws.com/openpay.v1.min.js'
        );
    }
    if (window.OpenPay && !window.OpenPay.deviceData) {
        await loadSingleScript(
            'https://js.openpay.pe/openpay-data.v1.min.js',
            'https://openpay.s3.amazonaws.com/openpay-data.v1.min.js'
        );
    }
    return !!window.OpenPay;
};

const ClientOpenpayCheckout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const initialInvoiceId = searchParams.get('invoice_id') || '';
    const initialAmount = parseFloat(searchParams.get('amount') || '0');

    const [loadingInit, setLoadingInit] = useState(true);
    const [activeTab, setActiveTab] = useState('card'); // 'card' | 'digital' | 'agent'
    const [config, setConfig] = useState({
        merchantId: '',
        publicKey: '',
        sandboxMode: true,
        deviceSessionId: '',
        amount: initialAmount,
        invoiceId: initialInvoiceId
    });

    const [holderName, setHolderName] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [cvv2, setCvv2] = useState('');
    const [installments, setInstallments] = useState('1');

    const [submittingCard, setSubmittingCard] = useState(false);
    const [generatingCode, setGeneratingCode] = useState(false);

    const [storeCodeData, setStoreCodeData] = useState(null);
    const [copiedCode, setCopiedCode] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [statusInfoMessage, setStatusInfoMessage] = useState('');

    const [errorModal, setErrorModal] = useState({
        open: false,
        title: '',
        message: '',
        suggestion: ''
    });

    const [successModal, setSuccessModal] = useState({
        open: false,
        transactionId: ''
    });

    const returnToMyReceipts = (txId = '') => {
        const targetUrl = txId
            ? `/client/payments?openpay_paid=1&tx=${encodeURIComponent(txId)}`
            : '/client/payments';

        if (window.opener && !window.opener.closed) {
            try {
                window.opener.location.href = targetUrl;
                window.opener.focus();
                window.close();
                return;
            } catch (_) {}
        }
        navigate(targetUrl, { replace: true });
    };

    const showFriendlyError = (rawMessage, rawCode = '') => {
        const text = String(rawMessage || '').toLowerCase();
        const code = String(rawCode || '').toLowerCase();

        let title = '❌ Transacción No Aprobada';
        let message = rawMessage || 'No se pudo completar el cobro con la tarjeta seleccionada.';
        let suggestion = 'Verifica los datos ingresados o intenta con otro método de pago.';

        if (code === '1014' || code === '1005' || text.includes('inactive') || text.includes('inactiva')) {
            title = '⚠️ Cuenta Openpay Inactiva (Sandbox)';
            message = 'La cuenta de comercio en Openpay Sandbox se encuentra inactiva.';
            suggestion = 'Verifica tus credenciales en el Portal Admin o inicia sesión en sandbox-dashboard.openpay.pe para reactivar el Merchant ID.';
        } else if (code === '3003' || text.includes('insufficient') || text.includes('saldo') || text.includes('fondos')) {
            title = '💳 Fondos Insuficientes';
            message = 'La tarjeta no tiene saldo disponible.';
            suggestion = 'Verifica el saldo disponible de tu cuenta o usa la opción de Pago digital / Agentes.';
        } else if (code === '3002' || code === '2005' || text.includes('expired') || text.includes('vencida') || text.includes('expirada')) {
            title = '📅 Tarjeta Expirada';
            message = 'La tarjeta está vencida.';
            suggestion = 'Revisa el mes y año de vencimiento ingresados o intenta con una tarjeta vigente.';
        } else if (code === '3001' || code === '3004' || code === '3005' || text.includes('declined') || text.includes('declinada') || text.includes('rechaz')) {
            title = '🚫 Rechazo del Banco Emisor';
            message = 'Rechazo por el banco emisor.';
            suggestion = 'Consulta con tu banco emisor para autorizar la compra o intenta con otra tarjeta.';
        }

        setErrorModal({ open: true, title, message, suggestion });
    };

    useEffect(() => {
        document.title = 'Pago con Openpay by BBVA';
        const initOpenpay = async () => {
            if (!initialInvoiceId) {
                setLoadingInit(false);
                showFriendlyError('No se especificó el recibo a cancelar.');
                return;
            }
            try {
                const token = localStorage.getItem('clientToken');
                const response = await fetch('/api/client/payments/online/preference', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        invoice_id: initialInvoiceId,
                        gateway_type: 'openpay'
                    })
                });
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Error al inicializar Openpay');
                }

                await loadOpenpayScripts();
                let deviceSessionId = `sess_${Date.now()}`;
                if (window.OpenPay) {
                    window.OpenPay.setId(data.merchantId);
                    window.OpenPay.setApiKey(data.publicKey);
                    window.OpenPay.setSandboxMode(!!data.sandboxMode);
                    if (window.OpenPay.deviceData && typeof window.OpenPay.deviceData.setup === 'function') {
                        try {
                            deviceSessionId = window.OpenPay.deviceData.setup();
                        } catch (devErr) {
                            deviceSessionId = `sess_${Date.now()}`;
                        }
                    }
                }

                setConfig({
                    merchantId: data.merchantId || '',
                    publicKey: data.publicKey || '',
                    sandboxMode: !!data.sandboxMode,
                    deviceSessionId,
                    amount: data.amount || initialAmount,
                    invoiceId: data.invoiceId || initialInvoiceId
                });
            } catch (err) {
                showFriendlyError(err.message || 'No se pudo cargar la configuración de Openpay.');
            } finally {
                setLoadingInit(false);
            }
        };

        initOpenpay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmitCard = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const holder = (holderName || '').trim();
        const rawCard = (cardNumber || '').replace(/\s+/g, '');
        const expParts = (expirationDate || '').split('/');
        const expMonth = (expParts[0] || '').trim();
        const expYear = (expParts[1] || '').trim().slice(-2);
        const cvv = (cvv2 || '').trim();

        if (!holder || rawCard.length < 13 || !expMonth || !expYear || cvv.length < 3) {
            showFriendlyError('Por favor completa todos los campos de la tarjeta (Nombre del Titular, Fecha MM/AA, Número de la tarjeta y Código de seguridad).');
            return;
        }

        if (!window.OpenPay) {
            const loaded = await loadOpenpayScripts();
            if (!loaded) {
                showFriendlyError('No se pudo cargar la librería oficial OpenPay.js.');
                return;
            }
        }

        try {
            setSubmittingCard(true);
            window.OpenPay.setId(config.merchantId);
            window.OpenPay.setApiKey(config.publicKey);
            window.OpenPay.setSandboxMode(!!config.sandboxMode);

            window.OpenPay.token.create(
                {
                    card_number: rawCard,
                    holder_name: holder,
                    expiration_year: expYear,
                    expiration_month: expMonth.padStart(2, '0'),
                    cvv2: cvv
                },
                async (response) => {
                    const tokenId = response?.data?.id;
                    try {
                        const token = localStorage.getItem('clientToken');
                        const chargeRes = await fetch('/api/client/payments/online/openpay/charge', {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                method: 'card',
                                token_id: tokenId,
                                device_session_id: config.deviceSessionId || `sess_${Date.now()}`,
                                installments,
                                invoice_id: config.invoiceId
                            })
                        });

                        const chargeData = await chargeRes.json();
                        setSubmittingCard(false);

                        if (chargeRes.ok && chargeData.requiresRedirect && chargeData.redirectUrl) {
                            if (chargeData.transactionId) {
                                sessionStorage.setItem('openpay_pending_tx', chargeData.transactionId);
                            }
                            window.location.href = chargeData.redirectUrl;
                            return;
                        }

                        if (chargeRes.ok && chargeData.success) {
                            const txId = chargeData.transactionId || '';
                            setSuccessModal({
                                open: true,
                                transactionId: txId
                            });
                            setTimeout(() => {
                                returnToMyReceipts(txId);
                            }, 1200);
                        } else {
                            showFriendlyError(chargeData.message || 'El pago no fue aprobado.', chargeData.errorCode);
                        }
                    } catch (apiErr) {
                        setSubmittingCard(false);
                        showFriendlyError('Error de conexión al procesar el pago con el servidor.');
                    }
                },
                (errResponse) => {
                    setSubmittingCard(false);
                    const desc = errResponse?.data?.description || errResponse?.message || 'Datos de tarjeta inválidos.';
                    const code = errResponse?.data?.error_code || '';
                    showFriendlyError(desc, code);
                }
            );
        } catch (err) {
            setSubmittingCard(false);
            showFriendlyError(err.message || 'Error al tokenizar la tarjeta en Openpay.');
        }
    };

    const handleGenerateStoreCode = async () => {
        try {
            setGeneratingCode(true);
            setStatusInfoMessage('');
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/payments/online/openpay/charge', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    invoice_id: config.invoiceId,
                    method: 'store'
                })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Error al generar el código de pago en Openpay');
            }

            setStoreCodeData({
                reference: data.reference || '',
                barcodeUrl: data.barcodeUrl || '',
                pdfUrl: data.pdfUrl || '',
                transactionId: data.transactionId || '',
                amount: data.amount || config.amount || 0,
                dueDate: data.dueDate || ''
            });
        } catch (err) {
            showFriendlyError(err.message || 'No se pudo generar el código de pago en Openpay.');
        } finally {
            setGeneratingCode(false);
        }
    };

    const handleVerifyStorePayment = async () => {
        if (!storeCodeData?.transactionId) return;
        try {
            setCheckingStatus(true);
            setStatusInfoMessage('');
            const token = localStorage.getItem('clientToken');
            const res = await fetch('/api/client/payments/online/openpay/confirm', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ transaction_id: storeCodeData.transactionId })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                const txId = storeCodeData.transactionId || '';
                setSuccessModal({
                    open: true,
                    transactionId: txId
                });
                setTimeout(() => {
                    returnToMyReceipts(txId);
                }, 1200);
            } else if (res.ok && ['charge_pending', 'in_progress', 'pending'].includes(data.status)) {
                setStatusInfoMessage('Tu código de pago aún se encuentra pendiente de abono. Apenas realices el pago en tu App Bancaria o Agente, tu servicio se activará automáticamente.');
            } else {
                showFriendlyError(data.message || 'El pago aún no figura completado.');
            }
        } catch (err) {
            showFriendlyError('No se pudo verificar el estado del pago en este momento.');
        } finally {
            setCheckingStatus(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                bgcolor: '#ffffff',
                color: '#003366',
                position: 'relative',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 4, md: 6 },
                px: { xs: 2, sm: 4 }
            }}
        >
            {/* Aro celeste decorativo en la esquina superior derecha de la página (idéntico a la imagen oficial de Openpay) */}
            <Box
                sx={{
                    position: 'fixed',
                    top: { xs: -140, md: -130 },
                    right: { xs: -140, md: -110 },
                    width: { xs: 280, md: 420 },
                    height: { xs: 280, md: 420 },
                    borderRadius: '50%',
                    border: { xs: '42px solid #00cfff', md: '64px solid #00cfff' },
                    pointerEvents: 'none',
                    zIndex: 0
                }}
            />

            {/* Contenedor Principal Centrado */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 740,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Box mb={1.5}>
                    <Button
                        size="small"
                        onClick={() => returnToMyReceipts('')}
                        sx={{
                            color: '#004481',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            px: 1,
                            '&:hover': { bgcolor: 'rgba(0, 68, 129, 0.06)' }
                        }}
                    >
                        ← Volver a Mis Recibos
                    </Button>
                </Box>

                {/* Cabecera: "Pago con Openpay by BBVA" */}
                <Box display="flex" alignItems="center" gap={2} mb={2.5} flexWrap="wrap">
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.35rem', sm: '1.55rem' },
                            color: '#003366',
                            letterSpacing: '-0.3px',
                            fontFamily: 'Arial, sans-serif'
                        }}
                    >
                        Pago con
                    </Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 48 }}>
                        <svg width="185" height="48" viewBox="0 0 210 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="22" cy="22" r="17" fill="#003366" />
                            <path d="M13 21C13 16 17 12 22 12C27 12 31 16 31 21C31 27 22 33 22 33C22 33 13 27 13 21Z" fill="#00c8b3" />
                            <text x="44" y="29" fill="#003366" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="27" letterSpacing="-0.8">pen</text>
                            <text x="93" y="29" fill="#00c8b3" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="27" letterSpacing="-0.8">pay</text>
                            <text x="96" y="43" fill="#004481" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="10.5">by </text>
                            <text x="111" y="43" fill="#004481" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="11" letterSpacing="0.6">BBVA</text>
                        </svg>
                    </Box>
                </Box>

                {/* Subtítulo con el importe */}
                <Typography
                    sx={{
                        fontSize: { xs: '1rem', sm: '1.15rem' },
                        color: '#003366',
                        mb: 3,
                        lineHeight: 1.45,
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    <Box component="span" sx={{ fontWeight: 800 }}>
                        Vas a pagar el importe de S/ {Number(config.amount || 0).toFixed(2)}.
                    </Box>{' '}
                    <Box component="span" sx={{ fontWeight: 400, color: '#004481' }}>
                        Selecciona el método de pago que quieres usar:
                    </Box>
                </Typography>

                {/* Las 3 tarjetas de selección de método */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                        gap: 2.2,
                        mb: 3
                    }}
                >
                    {/* 1. Tarjeta de crédito o débito */}
                    <Box
                        onClick={() => setActiveTab('card')}
                        sx={{
                            bgcolor: '#e7f3fb',
                            borderRadius: '14px',
                            border: activeTab === 'card' ? '2.5px solid #003366' : '2.5px solid transparent',
                            p: 2.5,
                            minHeight: { xs: 120, sm: 175 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            '&:hover': {
                                borderColor: '#003366'
                            }
                        }}
                    >
                        <Box sx={{ mb: 1.5 }}>
                            <svg width="96" height="68" viewBox="0 0 86 62" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="24" y="6" width="54" height="36" rx="4" fill="#d0e2f2" />
                                <rect x="20" y="3" width="54" height="36" rx="4" fill="#003366" />
                                <rect x="20" y="10" width="54" height="7" fill="#001f42" />
                                <rect x="60" y="25" width="8" height="5" rx="1" fill="#ffffff" />
                                <rect x="12" y="20" width="54" height="36" rx="4" fill="#d0e2f2" />
                                <rect x="8" y="16" width="54" height="36" rx="4" fill="#8fd0f9" />
                                <rect x="14" y="23" width="12" height="8" rx="1.5" fill="#d9f0ff" />
                                <rect x="14" y="37" width="40" height="4" rx="1" fill="#ffffff" />
                                <rect x="14" y="44" width="24" height="3.5" rx="1" fill="#ffffff" />
                            </svg>
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: '#003366', lineHeight: 1.28 }}>
                            Tarjeta de crédito o débito
                        </Typography>
                    </Box>

                    {/* 2. Pago digital a través de la App o Web de tu banco */}
                    <Box
                        onClick={() => setActiveTab('digital')}
                        sx={{
                            bgcolor: '#e7f3fb',
                            borderRadius: '14px',
                            border: activeTab === 'digital' ? '2.5px solid #003366' : '2.5px solid transparent',
                            p: 2.5,
                            minHeight: { xs: 120, sm: 175 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            '&:hover': {
                                borderColor: '#003366'
                            }
                        }}
                    >
                        <Box sx={{ mb: 1.5 }}>
                            <svg width="80" height="68" viewBox="0 0 72 62" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="25" y="7" width="28" height="48" rx="4" fill="#c9dfee" />
                                <rect x="19" y="3" width="28" height="48" rx="4" fill="#003366" />
                                <rect x="19" y="8" width="28" height="37" fill="#3a99e8" />
                                <circle cx="33" cy="26.5" r="8.5" fill="#4cd964" />
                                <path d="M29.2 26.5L31.8 29.1L37.2 23.7" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#003366', lineHeight: 1.28 }}>
                            Pago digital a través de la App o Web de tu banco
                        </Typography>
                    </Box>

                    {/* 3. Pago presencial en agentes, agencias o establecimientos afiliados */}
                    <Box
                        onClick={() => setActiveTab('agent')}
                        sx={{
                            bgcolor: '#e7f3fb',
                            borderRadius: '14px',
                            border: activeTab === 'agent' ? '2.5px solid #003366' : '2.5px solid transparent',
                            p: 2.5,
                            minHeight: { xs: 120, sm: 175 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            '&:hover': {
                                borderColor: '#003366'
                            }
                        }}
                    >
                        <Box sx={{ mb: 1.5 }}>
                            <svg width="96" height="68" viewBox="0 0 86 62" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="18" y="13" width="56" height="40" rx="2" fill="#d0e2f2" />
                                <rect x="14" y="14" width="54" height="34" fill="#1b75bc" />
                                <rect x="11" y="7" width="60" height="8" rx="2" fill="#003366" />
                                <rect x="19" y="21" width="24" height="19" fill="#8fd0f9" />
                                <rect x="48" y="21" width="15" height="27" fill="#5aaef2" />
                                <line x1="55.5" y1="21" x2="55.5" y2="48" stroke="#1b75bc" strokeWidth="1.5" />
                                <rect x="9" y="46" width="64" height="4" rx="1.5" fill="#00c8b3" />
                            </svg>
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: '#003366', lineHeight: 1.25 }}>
                            Pago presencial en agentes, agencias o establecimientos afiliados
                        </Typography>
                    </Box>
                </Box>

                {/* Recuadro Inferior Redondeado */}
                <Box
                    sx={{
                        border: '1px solid #cbd5e1',
                        borderRadius: '14px',
                        bgcolor: '#ffffff',
                        p: { xs: 2.5, sm: 3.5 }
                    }}
                >
                    {loadingInit ? (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
                            <CircularProgress size={32} sx={{ color: '#004481', mb: 1.5 }} />
                            <Typography variant="body2" sx={{ color: '#004481', fontWeight: 600 }}>
                                Cargando pasarela Openpay...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* PESTAÑA 1: TARJETA DE CRÉDITO O DÉBITO */}
                            {activeTab === 'card' && (
                                <Box component="form" autoComplete="off" onSubmit={handleSubmitCard}>
                                    {/* Logos: VISA, Mastercard, American Express, Diners Club */}
                                    <Box display="flex" justifyContent="center" alignItems="center" gap={{ xs: 2.5, sm: 4 }} mb={1.5} flexWrap="wrap">
                                        <svg width="60" height="26" viewBox="0 0 64 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <text x="4" y="19" fill="#1a1f71" fontFamily="Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="20" letterSpacing="-0.5">VISA</text>
                                            <path d="M4 5L12 5L14 10" stroke="#f79e1b" strokeWidth="2.5" strokeLinecap="round" />
                                        </svg>
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <svg width="46" height="28" viewBox="0 0 46 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="17" cy="14" r="11" fill="#eb001b" />
                                                <circle cx="29" cy="14" r="11" fill="#f79e1b" fillOpacity="0.9" />
                                            </svg>
                                            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#222', mt: -0.4 }}>mastercard</Typography>
                                        </Box>
                                        <Box sx={{ bgcolor: '#2e77bc', px: 1.1, py: 0.6, borderRadius: '3px', textAlign: 'center', lineHeight: 1 }}>
                                            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.55rem', letterSpacing: '0.3px', lineHeight: 1.05 }}>
                                                AMERICAN<br />EXPRESS
                                            </Typography>
                                        </Box>
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <svg width="28" height="24" viewBox="0 0 28 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="14" cy="12" r="10" stroke="#004481" strokeWidth="2.2" />
                                                <path d="M11.5 7V17M16.5 7V17" stroke="#004481" strokeWidth="2.2" strokeLinecap="round" />
                                            </svg>
                                            <Typography sx={{ fontSize: '0.52rem', fontWeight: 800, color: '#003366', lineHeight: 1 }}>Diners Club</Typography>
                                            <Typography sx={{ fontSize: '0.38rem', fontWeight: 700, color: '#003366', lineHeight: 1 }}>INTERNATIONAL</Typography>
                                        </Box>
                                    </Box>

                                    <Typography align="center" sx={{ fontSize: '0.8rem', color: '#004481', fontWeight: 600, mb: 2.5 }}>
                                        Hasta 6 cuotas sin intereses con BBVA
                                    </Typography>

                                    <Grid container spacing={2} alignItems="center">
                                        {/* Nombre del Titular */}
                                        <Grid item xs={12} sm={6}>
                                            <input
                                                type="text"
                                                name="op_card_holder_custom"
                                                autoComplete="off"
                                                placeholder="Nombre del Titular"
                                                data-openpay-card="holder_name"
                                                value={holderName}
                                                onChange={(e) => setHolderName(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    height: '44px',
                                                    padding: '0 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '0.92rem',
                                                    color: '#1e293b',
                                                    backgroundColor: '#ffffff',
                                                    boxSizing: 'border-box',
                                                    outline: 'none',
                                                    textAlign: 'center'
                                                }}
                                            />
                                        </Grid>

                                        {/* Fecha de Expiración MM/AA */}
                                        <Grid item xs={12} sm={6}>
                                            <input
                                                type="text"
                                                name="op_card_exp_custom"
                                                autoComplete="off"
                                                placeholder="Fecha de Expiración MM/AA"
                                                data-openpay-card="expiration_date"
                                                maxLength={5}
                                                value={expirationDate}
                                                onChange={(e) => {
                                                    let v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                    if (v.length >= 3) {
                                                        v = `${v.slice(0, 2)}/${v.slice(2)}`;
                                                    }
                                                    setExpirationDate(v);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '44px',
                                                    padding: '0 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '0.92rem',
                                                    color: '#1e293b',
                                                    backgroundColor: '#ffffff',
                                                    boxSizing: 'border-box',
                                                    outline: 'none',
                                                    textAlign: 'center'
                                                }}
                                            />
                                        </Grid>

                                        {/* Número de la tarjeta */}
                                        <Grid item xs={12} sm={6}>
                                            <input
                                                type="text"
                                                name="op_card_num_custom"
                                                autoComplete="new-password"
                                                placeholder="Número de la tarjeta"
                                                data-openpay-card="card_number"
                                                maxLength={19}
                                                value={cardNumber}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\D/g, '').slice(0, 16);
                                                    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
                                                    setCardNumber(formatted);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '44px',
                                                    padding: '0 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '0.92rem',
                                                    color: '#1e293b',
                                                    backgroundColor: '#ffffff',
                                                    boxSizing: 'border-box',
                                                    outline: 'none',
                                                    textAlign: 'center'
                                                }}
                                            />
                                        </Grid>

                                        {/* Código de seguridad */}
                                        <Grid item xs={12} sm={6}>
                                            <input
                                                type="password"
                                                name="op_card_cvv_custom"
                                                autoComplete="new-password"
                                                placeholder="Código de seguridad"
                                                data-openpay-card="cvv2"
                                                maxLength={4}
                                                value={cvv2}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                    setCvv2(digits);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '44px',
                                                    padding: '0 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '0.92rem',
                                                    color: '#1e293b',
                                                    backgroundColor: '#ffffff',
                                                    boxSizing: 'border-box',
                                                    outline: 'none',
                                                    textAlign: 'center'
                                                }}
                                            />
                                        </Grid>

                                        {/* N° de Cuotas sin intereses */}
                                        <Grid item xs={12} sm={6}>
                                            <select
                                                value={installments}
                                                onChange={(e) => setInstallments(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    height: '44px',
                                                    padding: '0 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '0.92rem',
                                                    color: '#64748b',
                                                    backgroundColor: '#ffffff',
                                                    boxSizing: 'border-box',
                                                    outline: 'none',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <option value="1">N° de Cuotas sin intereses</option>
                                                <option value="2">2 cuotas</option>
                                                <option value="3">3 cuotas</option>
                                                <option value="6">6 cuotas</option>
                                                <option value="12">12 cuotas</option>
                                            </select>
                                        </Grid>

                                        {/* Botón Pagar */}
                                        <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={submittingCard}
                                                sx={{
                                                    bgcolor: '#004481',
                                                    '&:hover': { bgcolor: '#003366' },
                                                    color: '#ffffff',
                                                    fontWeight: 800,
                                                    textTransform: 'none',
                                                    fontSize: '1.05rem',
                                                    borderRadius: '7px',
                                                    height: 44,
                                                    minWidth: { xs: '100%', sm: 175 },
                                                    boxShadow: 'none'
                                                }}
                                            >
                                                {submittingCard ? <CircularProgress size={22} color="inherit" /> : 'Pagar'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* PESTAÑA 2 Y 3: PAGO DIGITAL O PAGO PRESENCIAL EN AGENTES */}
                            {(activeTab === 'digital' || activeTab === 'agent') && (
                                <Box textAlign="center" py={1}>
                                    {/* Logos de Bancos / Agentes */}
                                    <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
                                        <Chip label="BBVA" sx={{ bgcolor: '#004481', color: '#fff', fontWeight: 900, fontSize: '0.78rem', borderRadius: '6px' }} />
                                        <Chip label="BCP" sx={{ bgcolor: '#002a8d', color: '#ff7800', fontWeight: 900, fontSize: '0.78rem', borderRadius: '6px' }} />
                                        <Chip label="Interbank" sx={{ bgcolor: '#009b3a', color: '#fff', fontWeight: 900, fontSize: '0.78rem', borderRadius: '6px' }} />
                                        <Chip label="Scotiabank" sx={{ bgcolor: '#ec111a', color: '#fff', fontWeight: 900, fontSize: '0.78rem', borderRadius: '6px' }} />
                                        {activeTab === 'agent' && (
                                            <>
                                                <Chip label="Tambo+" sx={{ bgcolor: '#a61c80', color: '#ffde00', fontWeight: 900, fontSize: '0.78rem', borderRadius: '6px' }} />
                                                <Chip label="KasNet" sx={{ bgcolor: '#005691', color: '#fff', fontWeight: 900, fontSize: '0.78rem', borderRadius: '6px' }} />
                                            </>
                                        )}
                                    </Box>

                                    {activeTab === 'digital' ? (
                                        <Typography sx={{ fontSize: '0.92rem', color: '#334155', maxWidth: 540, mx: 'auto', mb: 2.5, lineHeight: 1.55 }}>
                                            En la App o Web de tu banco ingresa a la opción{' '}
                                            <Box component="span" sx={{ fontWeight: 800, color: '#003366' }}>"Pago de Servicios"</Box>, busca{' '}
                                            <Box component="span" sx={{ fontWeight: 800, color: '#003366' }}>"Kashio"</Box>, coloca el código de pago que llegará a tu correo electrónico y realiza el pago.
                                        </Typography>
                                    ) : (
                                        <Typography sx={{ fontSize: '0.92rem', color: '#334155', maxWidth: 540, mx: 'auto', mb: 2.5, lineHeight: 1.55 }}>
                                            Paga en agentes, agencias o establecimientos afiliados con el código de referencia que llegará a tu correo electrónico, indicando que deseas pagar{' '}
                                            <Box component="span" sx={{ fontWeight: 800, color: '#003366' }}>"Kashio"</Box>.
                                        </Typography>
                                    )}

                                    {!storeCodeData ? (
                                        <Button
                                            variant="contained"
                                            onClick={handleGenerateStoreCode}
                                            disabled={generatingCode}
                                            sx={{
                                                bgcolor: '#004481',
                                                '&:hover': { bgcolor: '#003366' },
                                                color: '#ffffff',
                                                fontWeight: 800,
                                                textTransform: 'none',
                                                fontSize: '1rem',
                                                borderRadius: '7px',
                                                px: 4,
                                                py: 1.2,
                                                boxShadow: 'none'
                                            }}
                                        >
                                            {generatingCode ? <CircularProgress size={22} color="inherit" /> : 'Genera tu código'}
                                        </Button>
                                    ) : (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 2.5,
                                                borderRadius: '12px',
                                                bgcolor: '#f8fafc',
                                                border: '2px dashed #0284c7',
                                                maxWidth: 500,
                                                mx: 'auto'
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#004481', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', mb: 0.5 }}>
                                                Código de Pago Generado (Kashio / Openpay)
                                            </Typography>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} my={1}>
                                                <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 900, color: '#003366', letterSpacing: '1.5px' }}>
                                                    {storeCodeData.reference}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant={copiedCode ? 'contained' : 'outlined'}
                                                    color={copiedCode ? 'success' : 'primary'}
                                                    startIcon={copiedCode ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(storeCodeData.reference);
                                                        setCopiedCode(true);
                                                        setTimeout(() => setCopiedCode(false), 2500);
                                                    }}
                                                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px' }}
                                                >
                                                    {copiedCode ? 'Copiado' : 'Copiar'}
                                                </Button>
                                            </Box>

                                            {storeCodeData.barcodeUrl && (
                                                <Box sx={{ my: 1.5, bgcolor: '#fff', p: 1, borderRadius: '8px', display: 'inline-block', border: '1px solid #e2e8f0' }}>
                                                    <img
                                                        src={storeCodeData.barcodeUrl}
                                                        alt="Código de barras Openpay"
                                                        style={{ maxWidth: '100%', height: 46, display: 'block' }}
                                                    />
                                                </Box>
                                            )}

                                            {statusInfoMessage && (
                                                <Alert severity="info" sx={{ my: 1.5, textAlign: 'left', borderRadius: '8px' }}>
                                                    {statusInfoMessage}
                                                </Alert>
                                            )}

                                            <Box display="flex" flexDirection="column" gap={1} mt={1.5}>
                                                {storeCodeData.pdfUrl && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<DownloadIcon />}
                                                        component="a"
                                                        href={storeCodeData.pdfUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px' }}
                                                    >
                                                        Descargar Recibo Oficial PDF (Openpay)
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="contained"
                                                    onClick={handleVerifyStorePayment}
                                                    disabled={checkingStatus}
                                                    sx={{
                                                        bgcolor: '#004481',
                                                        '&:hover': { bgcolor: '#003366' },
                                                        textTransform: 'none',
                                                        fontWeight: 800,
                                                        borderRadius: '7px',
                                                        py: 1.1
                                                    }}
                                                >
                                                    {checkingStatus ? <CircularProgress size={20} color="inherit" /> : 'Ya realicé mi pago (Verificar Estado)'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* Modal de Error */}
            <Dialog
                open={errorModal.open}
                onClose={() => setErrorModal(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 1, textAlign: 'center' } }}
            >
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            bgcolor: '#fef2f2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '4px solid #fee2e2'
                        }}>
                            <CloseIcon sx={{ fontSize: 36, color: '#ef4444' }} />
                        </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#991b1b' }}>
                        {errorModal.title}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#dc2626', mb: 2 }}>
                        {errorModal.message}
                    </Typography>
                    <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 3, border: '1px solid #e2e8f0', textAlign: 'left' }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            💡 Sugerencia:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                            {errorModal.suggestion}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ pb: 2.5, px: 3 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => setErrorModal(prev => ({ ...prev, open: false }))}
                        sx={{ bgcolor: '#004481', '&:hover': { bgcolor: '#003366' }, borderRadius: 2.5, py: 1.2, fontWeight: 'bold', textTransform: 'none' }}
                    >
                        Entendido
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Éxito */}
            <Dialog
                open={successModal.open}
                onClose={() => {}}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 1, textAlign: 'center' } }}
            >
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            bgcolor: '#ecfdf5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '4px solid #a7f3d0'
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981' }} />
                        </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#065f46' }}>
                        ¡Pago Exitoso con Openpay!
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#047857', mb: 2 }}>
                        Su pago con Openpay (BBVA) fue procesado exitosamente. El recibo ha sido cancelado y su servicio está activo.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pb: 2.5, px: 3 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => returnToMyReceipts(successModal.transactionId)}
                        sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 2.5, py: 1.2, fontWeight: 'bold', textTransform: 'none' }}
                    >
                        Volver a Mis Recibos
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClientOpenpayCheckout;
